import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Building2, CheckCircle2, MapPin, DollarSign, ArrowRight,
  Calendar, Briefcase, Home as HomeIcon, BedDouble, Users, Tag,
  Phone, Mail, Globe, Sparkles,
} from "lucide-react";
import { SITE } from "@/lib/site";

const HERO_IMG =
  "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=2000&q=80";
const INTRO_IMG =
  "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1400&q=80";

const REASONS = [
  { icon: Calendar, title: "30+ Years of Experience", text: "Decades of professional cleaning knowledge applied to every property we serve." },
  { icon: Briefcase, title: "Commercial & Residential", text: "One dependable company for offices, homes, rentals, and everything in between." },
  { icon: HomeIcon, title: "Flexible Scheduling", text: "Weekly, bi-weekly, monthly, or one-time service — on your timeline." },
  { icon: BedDouble, title: "Vacation Rental Specialists", text: "Fast, reliable turnovers that protect your five-star reviews." },
  { icon: Users, title: "Trusted by Property Managers", text: "We understand portfolio needs: quick turns, clean results, and clear communication." },
  { icon: Tag, title: "Competitive, Transparent Pricing", text: "Starting at $0.12/sq ft. Free estimates. No hidden fees." },
];

const SPOTLIGHTS = [
  { icon: Building2, title: "Commercial Cleaning", text: "Offices, workplaces, and commercial facilities. Recurring programs and custom scopes built for business operations.", anchor: "#commercial" },
  { icon: HomeIcon, title: "Residential Cleaning", text: "Homes, apartments, and condos. Standard cleaning, deep cleaning, move-in/move-out, and recurring housekeeping.", anchor: "#residential" },
  { icon: BedDouble, title: "Airbnb & Rental Turnovers", text: "Guest-ready cleaning for short-term rental hosts and property managers. Fast, thorough, and dependable.", anchor: "#airbnb" },
];

export default function Home() {
  useEffect(() => {
    document.title = "Eco Ready Solutions, LLC | Professional Cleaning Services in Royal Oak, MI";
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); return m;
    })();
    meta.content = "Professional commercial and residential cleaning services in Royal Oak, MI. 30+ years of experience. Offices, homes, Airbnb turnovers, move-in/move-out, and property management. Starting at $0.12/sq ft. Free estimates.";
  }, []);

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative h-[640px] md:h-[720px] w-full flex items-center"
        style={{ backgroundImage: `url(${HERO_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="absolute inset-0 eco-hero-overlay" />
        <div className="relative eco-container">
          <span className="eco-eyebrow text-white/85 eco-reveal">Royal Oak, Michigan · Est. with 30+ Years of Experience</span>
          <h1 className="eco-h1 text-white mt-4 max-w-4xl eco-reveal eco-reveal-d1">
            Professional Cleaning.<br />Dependable Results.<br />
            <span style={{ color: "#A7D3B9" }}>Let&apos;s Be Clean Together.</span>
          </h1>
          <p className="text-white/90 text-lg md:text-xl mt-6 max-w-2xl eco-reveal eco-reveal-d2">
            Eco Ready Solutions, LLC delivers commercial and residential cleaning services across Metro Detroit
            and Oakland County — backed by over 30 years of professional cleaning experience.
          </p>
          <div className="mt-9 flex flex-wrap gap-3 eco-reveal eco-reveal-d3">
            <Link to="/contact" data-testid="hero-cta-estimate" className="eco-btn eco-btn-primary">
              Request a Free Estimate <ArrowRight size={17} />
            </Link>
            <Link to="/services" data-testid="hero-cta-services" className="eco-btn eco-btn-outline">
              View Our Services
            </Link>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section data-testid="trust-bar" className="border-y border-[#D6DDD9] bg-white">
        <div className="eco-container grid grid-cols-2 md:grid-cols-4 gap-6 py-7">
          {[
            { icon: Building2, label: "Commercial & Residential" },
            { icon: CheckCircle2, label: "30+ Years of Experience" },
            { icon: MapPin, label: "Metro Detroit & Oakland County" },
            { icon: DollarSign, label: "Starting at $0.12/sq ft" },
          ].map((t, i) => {
            const Icon = t.icon;
            return (
              <div key={i} className="flex items-center gap-3">
                <span className="eco-icon-wrap" style={{ width: 40, height: 40 }}><Icon size={20} /></span>
                <span className="text-[14.5px] font-medium text-[#1A1A1A]">{t.label}</span>
              </div>
            );
          })}
        </div>
      </section>

      {/* INTRO */}
      <section className="eco-section bg-white">
        <div className="eco-container grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <span className="eco-eyebrow">About Eco Ready Solutions</span>
            <h2 className="eco-h2 mt-3">Your Professional Cleaning Partner</h2>
            <p className="mt-5 text-[#1A1A1A] text-[17px] leading-[1.75]">
              At Eco Ready Solutions, we believe a clean space is the foundation of a productive business and a peaceful home.
              Whether you manage a commercial office, own rental properties, host short-term guests, or simply want a beautifully
              maintained home, we are here to make it happen — professionally, consistently, and on your schedule.
            </p>
            <p className="mt-4 text-[#555555] text-[16px] leading-[1.75]">
              Based in Royal Oak, Michigan, we proudly serve clients throughout Metro Detroit and Oakland County with a full
              range of cleaning services tailored to your exact needs.
            </p>
            <div className="mt-7">
              <Link to="/about" className="eco-link-arrow">Learn more about us <ArrowRight size={15} /></Link>
            </div>
          </div>
          <div className="relative">
            <div className="relative rounded-[14px] overflow-hidden border border-[#D6DDD9]">
              <img src={INTRO_IMG} alt="Bright, professionally cleaned interior" className="w-full h-[440px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="eco-section" style={{ background: "#F4F6F5" }}>
        <div className="eco-container">
          <div className="max-w-2xl">
            <span className="eco-eyebrow">Why Eco Ready</span>
            <h2 className="eco-h2 mt-3">Why Clients Choose Eco Ready Solutions</h2>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {REASONS.map((r, i) => {
              const Icon = r.icon;
              return (
                <div key={i} data-testid={`why-card-${i}`} className="eco-card">
                  <span className="eco-icon-wrap"><Icon size={22} /></span>
                  <h3 className="eco-h3 mt-5">{r.title}</h3>
                  <p className="mt-2 text-[#555555] text-[15px] leading-relaxed">{r.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICE SPOTLIGHTS */}
      <section className="eco-section bg-white">
        <div className="eco-container">
          <div className="max-w-2xl">
            <span className="eco-eyebrow">Our Services</span>
            <h2 className="eco-h2 mt-3">How We Can Help You</h2>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {SPOTLIGHTS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} data-testid={`spotlight-${i}`} className="eco-card flex flex-col">
                  <span className="eco-icon-wrap"><Icon size={22} /></span>
                  <h3 className="eco-h3 mt-5">{s.title}</h3>
                  <p className="mt-2 text-[#555555] text-[15px] leading-relaxed flex-1">{s.text}</p>
                  <Link to={`/services${s.anchor}`} className="eco-link-arrow mt-5">Learn More <ArrowRight size={15} /></Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRICING PREVIEW */}
      <section className="eco-section text-white" style={{ background: "#2D6A4F" }}>
        <div className="eco-container grid lg:grid-cols-[1.2fr_1fr] gap-10 items-center">
          <div>
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/80">Pricing</span>
            <h2 className="eco-h2 text-white mt-3">Professional Cleaning. Honest Pricing.</h2>
            <p className="mt-5 text-white/90 text-[17px] leading-[1.8] max-w-2xl">
              Our professional cleaning services begin at <strong>$0.12 per square foot</strong>. Final pricing is determined by
              the property&apos;s size, condition, cleaning frequency, and scope of services required. We are committed to earning
              your business — and are often able to provide competitive rates for clients who share their current cleaning cost.
            </p>
          </div>
          <div className="flex lg:justify-end">
            <Link to="/contact" data-testid="pricing-cta-estimate" className="eco-btn"
              style={{ background: "#fff", color: "#2D6A4F" }}>
              Request Your Free Estimate <ArrowRight size={17} />
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="eco-section" style={{ background: "#F4F6F5" }}>
        <div className="eco-container text-center max-w-3xl mx-auto">
          <span className="eco-icon-wrap mx-auto"><Sparkles size={22} /></span>
          <h2 className="eco-h2 mt-5">Ready to Experience the Difference?</h2>
          <p className="mt-4 text-[#555555] text-[17px] leading-[1.75]">
            Contact Eco Ready Solutions today and let&apos;s build a cleaning plan that works for your property, your schedule,
            and your budget. <span className="text-[#2D6A4F] font-semibold">Let&apos;s Be Clean Together.</span>
          </p>
          <div className="mt-8 grid sm:grid-cols-3 gap-3 max-w-2xl mx-auto">
            <a href={SITE.phoneHref} className="flex items-center justify-center gap-2 bg-white border border-[#D6DDD9] rounded-[10px] py-4 text-[14.5px] font-medium hover:border-[#2D6A4F] transition-colors">
              <Phone size={16} className="text-[#2D6A4F]" /> {SITE.phone}
            </a>
            <a href={SITE.emailHref} className="flex items-center justify-center gap-2 bg-white border border-[#D6DDD9] rounded-[10px] py-4 text-[14.5px] font-medium hover:border-[#2D6A4F] transition-colors">
              <Mail size={16} className="text-[#2D6A4F]" /> {SITE.email}
            </a>
            <a href={SITE.websiteHref} className="flex items-center justify-center gap-2 bg-white border border-[#D6DDD9] rounded-[10px] py-4 text-[14.5px] font-medium hover:border-[#2D6A4F] transition-colors">
              <Globe size={16} className="text-[#2D6A4F]" /> {SITE.website}
            </a>
          </div>
          <div className="mt-8">
            <Link to="/contact" data-testid="final-cta-estimate" className="eco-btn eco-btn-primary">
              Get a Free Estimate <ArrowRight size={17} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
